// Reusable Flatpickr date-range initializer
// Works with wrapper [data-date-range-picker], expects data-start-hidden and data-end-hidden selectors

function computeMinDate(anchorSelector) {
  const anchor = anchorSelector ? document.querySelector(anchorSelector) : null;
  if (anchor && anchor.value) {
    const d = new Date(anchor.value);
    if (!isNaN(d)) {
      d.setDate(d.getDate() + 1);
      return d;
    }
  }
  return 'today';
}

// Parse 'YYYY-MM-DD' string format to Date object
function parseYMDLocal(ymd) {
  if (!ymd) return null;
  try {
    // Parse 'YYYY-MM-DD' format to Date object in local timezone
    const parts = ymd.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(ymd);
  } catch(_) {
    return null;
  }
}

function parseSelectorList(list) {
  if (!list) return [];
  try {
    return list
      .split(',')
      .map(s => s.trim())
      .map(s => s.replace(/^['"]+|['"]+$/g, '')) // strip wrapping quotes
      .map(s => s.replace(/[\n\r]/g, ''))
      .filter(Boolean);
  } catch (_) {
    return [];
  }
}

function anySelectorsHaveValue(selectors) {
  if (!selectors || selectors.length === 0) return false;
  for (let sel of selectors) {
    // extra safety: strip stray quotes
    sel = sel.replace(/^['"]+|['"]+$/g, '');
    const nodeList = document.querySelectorAll(sel);
    for (const el of nodeList) {
      const val = (el && 'value' in el) ? String(el.value || '').trim() : '';
      if (val !== '') return true;
    }
  }
  return false;
}

function allSelectorsHaveValue(selectors) {
  if (!selectors || selectors.length === 0) return true;
  for (let sel of selectors) {
    sel = sel.replace(/^['"]+|['"]+$/g, '');
    const nodeList = document.querySelectorAll(sel);
    if (nodeList.length === 0) return false;
    let anyNonEmpty = false;
    for (const el of nodeList) {
      const val = (el && 'value' in el) ? String(el.value || '').trim() : '';
      if (val !== '') { anyNonEmpty = true; break; }
    }
    if (!anyNonEmpty) return false;
  }
  return true;
}

function clearBySelectors(selectors) {
  if (!selectors) return;
  for (const sel of selectors) {
    const nodeList = document.querySelectorAll(sel);
    for (const el of nodeList) {
      if ('value' in el) el.value = '';
      // Dispatch input event to notify listeners
      try { el.dispatchEvent(new Event('input', { bubbles: true })); } catch(_) {}
      try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch(_) {}
    }
  }
}

function disableBySelectors(selectors) {
  if (!selectors) return;
  for (const sel of selectors) {
    const nodeList = document.querySelectorAll(sel);
    for (const el of nodeList) {
      try { el.disabled = true; } catch(_) {}
    }
  }
}

// Use message exactly as provided via data-confirm-message

function clearErrorsBySelectors(selectors) {
  if (!selectors) return;
  for (const sel of selectors) {
    const nodeList = document.querySelectorAll(sel);
    for (const el of nodeList) {
      const id = el && el.id ? el.id : null;
      if (id) {
        // Remove invalid style on the input
        try { el.classList.remove('is-invalid'); } catch(_) {}
        // Remove group invalid if present
        try {
          const group = el.closest('.seamless-group') || el.closest('.range-group');
          if (group) {
            group.classList.remove('invalid');
            group.classList.remove('is-invalid');
          }
        } catch(_) {}
        // Hide feedback element if following the *_feedback convention
        try {
          const fb = document.getElementById(id + '_feedback');
          if (fb) { fb.style.display = 'none'; fb.textContent = ''; }
        } catch(_) {}
      }
    }
  }
}

// Check if new event dates would cause validation conflicts with existing registration dates
// Returns an object with conflict information: { hasConflict: boolean, conflictType: string, conflictField: string }
function wouldCauseValidationConflict(newEventStartDate, newEventEndDate) {
  const regStartDate = document.querySelector('#registration_start_date')?.value;
  const regEndDate = document.querySelector('#registration_end_date')?.value;
  const regStartTime = document.querySelector('#registration_start_time')?.value;
  const regEndTime = document.querySelector('#registration_end_time')?.value;
  const eventStartTime = document.querySelector('#event_start_time')?.value;
  const eventEndTime = document.querySelector('#event_end_time')?.value;
  const publishAt = document.querySelector('#publish_at_hidden')?.value;

  // If no registration dates are set, no conflict
  if (!regStartDate && !regEndDate && !publishAt) {
    return { hasConflict: false, conflictType: '', conflictField: '' };
  }

  // Rule: Event start time must be after registration end time
  if (regEndDate && regEndTime && newEventStartDate && eventStartTime) {
    const regEndDateTime = new Date(regEndDate + 'T' + regEndTime);
    const eventStartDateTime = new Date(newEventStartDate + 'T' + eventStartTime);
    
    if (newEventStartDate === regEndDate) {
      // When dates are equal, event start time must be after registration end time
      if (eventStartDateTime <= regEndDateTime) {
        return { hasConflict: true, conflictType: 'time_before_registration_end', conflictField: 'Registration End Time' };
      }
    } else if (eventStartDateTime <= regEndDateTime) {
      return { hasConflict: true, conflictType: 'time_before_registration_end', conflictField: 'Registration End Time' };
    }
  }

  // Rule: Event start date must be after registration end date (date only check)
  if (regEndDate && newEventStartDate) {
    if (newEventStartDate < regEndDate) {
      return { hasConflict: true, conflictType: 'date_before_registration_end', conflictField: 'Registration End Date' };
    }
  }

  // Rule: Event end date must be after registration end date (date only check)
  if (regEndDate && newEventEndDate) {
    if (newEventEndDate < regEndDate) {
      return { hasConflict: true, conflictType: 'date_before_registration_end', conflictField: 'Registration End Date' };
    }
  }

  // Rule: Event start date must be on or after registration start date
  // This ensures events don't start before registration period begins
  if (regStartDate && newEventStartDate) {
    if (newEventStartDate < regStartDate) {
      return { hasConflict: true, conflictType: 'date_before_registration_start', conflictField: 'Registration Start Date' };
    }
  }

  // Rule: Event end date must be on or after registration start date
  // This ensures events don't end before registration period begins
  if (regStartDate && newEventEndDate) {
    if (newEventEndDate < regStartDate) {
      return { hasConflict: true, conflictType: 'date_before_registration_start', conflictField: 'Registration Start Date' };
    }
  }

  // Rule: Send Invitation At must be before Registration Start Time
  if (publishAt && regStartDate && regStartTime) {
    const publishAtDate = new Date(publishAt);
    const regStartDateTime = new Date(regStartDate + 'T' + regStartTime);
    if (publishAtDate > regStartDateTime) {
      return { hasConflict: true, conflictType: 'publish_after_registration', conflictField: 'Registration Start Time' };
    }
  }

  return { hasConflict: false, conflictType: '', conflictField: '' };
}

function initOne(wrapper) {
  if (wrapper.dataset.drpInitialized) return;
  const input = wrapper.querySelector('input.form-control');
  const startSel = wrapper.dataset.startHidden;
  const endSel = wrapper.dataset.endHidden;
  const startHidden = startSel ? document.querySelector(startSel) : null;
  const endHidden = endSel ? document.querySelector(endSel) : null;
  if (!input || !startHidden || !endHidden) return;

  wrapper.dataset.drpInitialized = '1';

  if (typeof flatpickr === 'undefined') {
    console.error('Flatpickr library is not loaded.');
    return;
  }

  // Prepopulate visible input from hidden values if both exist (robust parsing)
  let defaultDates = null;
  if (startHidden.value && endHidden.value) {
    const s = parseYMDLocal(startHidden.value);
    const e = parseYMDLocal(endHidden.value);
    if (s && e) {
      input.value = flatpickr.formatDate(s, 'd/m/Y') + ' - ' + flatpickr.formatDate(e, 'd/m/Y');
      defaultDates = [s, e];
    }
  }

  // Compute minDate, but don't enforce it if we have existing valid dates
  // This prevents existing dates from being rejected due to time component mismatch
  let minDate = computeMinDate('#registration_end_date');
  
  // If we have existing dates that are valid, don't enforce minDate constraint
  // This allows editing existing invitations without date conflicts
  if (defaultDates && defaultDates.length === 2) {
    // If minDate would reject our existing dates, use the earlier of the two
    if (minDate && minDate !== 'today') {
      const minDateTime = typeof minDate === 'string' ? new Date(minDate) : minDate;
      if (defaultDates[0] < minDateTime) {
        // Set minDate to the start date (at midnight) to allow the existing range
        minDate = new Date(defaultDates[0]);
        minDate.setHours(0, 0, 0, 0);
      }
    }
  }

  // Track previous values to allow cancel/restore
  let prevStart = startHidden.value;
  let prevEnd = endHidden.value;
  let prevVis = input.value;

  // Clear button plugin configuration
  const clearButtonPlugin = function(config) {
    return function(fp) {
      return {
        onReady() {
          const clearBtn = document.createElement('button');
          clearBtn.type = 'button';
          clearBtn.className = 'btn btn-sm btn-outline-secondary flatpickr-clear-btn';
          clearBtn.innerHTML = config.html || 'Clear';
          clearBtn.style.cssText = 'margin-left: 8px;';
          
          // Add clear button next to calendar container
          clearBtn.addEventListener('click', function(e) {
            e.preventDefault();
            fp.clear();
            // Clear hidden fields
            startHidden.value = '';
            endHidden.value = '';
            // Clear validation errors
            input.classList.remove('is-invalid');
            const group = input.closest('.range-group');
            if (group) group.classList.remove('invalid', 'is-invalid');
            const feedback = document.getElementById(input.id + '_feedback');
            if (feedback) { feedback.style.display = 'none'; feedback.textContent = ''; }
            // Trigger change event
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          
          // Append to calendar container
          if (fp.calendarContainer) {
            fp.calendarContainer.appendChild(clearBtn);
          }
        }
      };
    };
  };

  const fp = flatpickr(input, {
    mode: 'range',
    dateFormat: 'd/m/Y',
    allowInput: false,
    minDate: minDate,
    defaultDate: defaultDates,
    locale: { rangeSeparator: ' - ' },
    plugins: [
      clearButtonPlugin({
        html: '<i class="fas fa-times-circle"></i> Clear'
      })
    ],
    onReady: function(selectedDates, dateStr, instance) {
      // If field is disabled and we have defaultDates, ensure they're set
      if (input.disabled && defaultDates && defaultDates.length === 2) {
        instance.setDate(defaultDates, false);
      }
    },
    onOpen: function(selectedDates, dateStr, instance) {
      prevStart = startHidden.value;
      prevEnd = endHidden.value;
      prevVis = input.value;
    },
    onChange: function(selectedDates, dateStr, instance) {
      if (selectedDates.length === 2) {
        const s = selectedDates[0];
        const e = selectedDates[1];
        const newStart = flatpickr.formatDate(s, 'Y-m-d');
        const newEnd = flatpickr.formatDate(e, 'Y-m-d');
        // Update hidden fields immediately; confirmation handled in onClose for reliability
        startHidden.value = newStart;
        endHidden.value = newEnd;
        // remove invalid state if any
        input.classList.remove('is-invalid');
        const group = input.closest('.range-group');
        if (group) group.classList.remove('invalid');
        const feedback = document.getElementById(input.id + '_feedback');
        if (feedback) { feedback.style.display = 'none'; feedback.textContent = ''; }
      }
    },
    onClose: function(selectedDates, dateStr, instance) {
      const wrapperEl = wrapper;
      let confirmEnabled = String(wrapperEl.dataset.confirmClearOnChange || '').toLowerCase() === 'true';
      let msg = wrapperEl.dataset.confirmMessage || '';
      let clearTargets = parseSelectorList(wrapperEl.dataset.clearTargets || '');
      let disableTargets = parseSelectorList(wrapperEl.dataset.disableTargets || '');
      let checkSelectors = parseSelectorList(wrapperEl.dataset.checkSelectors || wrapperEl.dataset.clearTargets || '');
      let requireSelectors = parseSelectorList(wrapperEl.dataset.requireSelectors || '');

      // Fallback for Invitations page if data attrs missing
      if (!confirmEnabled && input.id === 'event_date_range') {
        confirmEnabled = true;
        if (checkSelectors.length === 0) {
          checkSelectors = parseSelectorList('#registration_start_datetime,#registration_end_datetime,#publish_at_display,#registration_start_date,#registration_start_time,#registration_end_date,#registration_end_time,#publish_at_hidden');
        }
        if (requireSelectors.length === 0) {
          requireSelectors = parseSelectorList('#event_start_date,#event_end_date,#event_start_time,#event_end_time');
        }
        if (!msg) msg = 'Changing the Event Date Range will clear Registration Start Time, Registration End Time, and Send Invitation At. Do you want to proceed?';
      }

      if (selectedDates.length < 2) {
        // clear hidden fields if incomplete
        startHidden.value = '';
        endHidden.value = '';
        return;
      }

      // Only confirm on actual change
      const s = selectedDates[0];
      const e = selectedDates[1];
      const newStart = flatpickr.formatDate(s, 'Y-m-d');
      const newEnd = flatpickr.formatDate(e, 'Y-m-d');
      const changed = (newStart !== prevStart) || (newEnd !== prevEnd);
      if (!changed) return;

      const hasDependents = anySelectorsHaveValue(checkSelectors);
      const eventReady = allSelectorsHaveValue(requireSelectors);
      const conflictInfo = wouldCauseValidationConflict(newStart, newEnd);
      
      if (confirmEnabled && hasDependents && eventReady && conflictInfo.hasConflict) {
        // Generate specific conflict message
        let conflictMessage = '';
        
        if (conflictInfo.conflictType === 'time_before_registration_end') {
          conflictMessage = `The event date range you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event start time must be after registration end time. Do you want to proceed?`;
        } else if (conflictInfo.conflictType === 'date_before_registration_end') {
          conflictMessage = `The event date range you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event dates must be after registration end date. Do you want to proceed?`;
        } else if (conflictInfo.conflictType === 'date_before_registration_start') {
          conflictMessage = `The event date range you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Event dates must be on or after registration start date. Do you want to proceed?`;
        } else if (conflictInfo.conflictType === 'publish_after_registration') {
          conflictMessage = `The event date range you set will conflict with ${conflictInfo.conflictField.toLowerCase()}. Send Invitation At must be before Registration Start Time. Do you want to proceed?`;
        } else {
          // Fallback to default message if conflict type is not recognized
          conflictMessage = (msg && String(msg)) || '';
        }
        
        const text = conflictMessage || (msg && String(msg)) || '';
        const proceed = window.confirm(text);
        if (!proceed) {
          // Convert string dates to Date objects before reverting to Flatpickr
          const prevStartDate = parseYMDLocal(prevStart);
          const prevEndDate = parseYMDLocal(prevEnd);
          
          if (prevStartDate && prevEndDate) {
            try { instance.setDate([prevStartDate, prevEndDate], true); } catch(_) {}
          }
          
          // Restore previous values
          input.value = prevVis;
          startHidden.value = prevStart;
          endHidden.value = prevEnd;
          
          // Dispatch change event to trigger updateRegistrationAndPublishAvailability()
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('input', { bubbles: true }));
          
          return;
        }
        clearBySelectors(clearTargets);
        disableBySelectors(disableTargets);
        clearErrorsBySelectors(clearTargets);
      }
      // Update previous snapshot after confirmed change
      prevStart = newStart;
      prevEnd = newEnd;
      prevVis = input.value;
    }
  });
  
  return fp;
}

export function initAllDateRangePickers() {
  document.querySelectorAll('[data-date-range-picker]').forEach(initOne);
}

document.addEventListener('turbolinks:load', initAllDateRangePickers);
document.addEventListener('DOMContentLoaded', initAllDateRangePickers);
